import { useMutation } from "@tanstack/react-query";
import { ApiError, requestAIDraft } from "@/lib/api";
import type {
  CoachAIDraftErrorDetail,
  CoachAIDraftRequest,
  CoachAIDraftResponse,
} from "@/lib/types";

/**
 * AI drafting mutation hook (M7.5).
 *
 * Wraps `POST /api/coach/ai/draft` with structured error handling
 * for the four backend codes the route returns under
 * `{ detail: { error_code, error_message } }`:
 *
 *   - `drafting_disabled` (403) — team-admin turned drafting off
 *   - `target_visibility_mismatch` (409) — composer's visibility
 *     conflicts with the target's required visibility
 *   - `unsupported_target_resource_type` (422)
 *   - `prompt_too_long` (413)
 *   - `draft_failed` (provider-side failure; rare)
 *
 * `apiFetch` already unwraps FastAPI's `detail` envelope (PR #11
 * fix), so `ApiError.detail` IS the `{error_code, error_message}`
 * object — the caller reads `aiError.code` to dispatch per-code
 * UX (the panel's gating layers are UX hints; the backend
 * re-enforces every gate).
 *
 * `invalidateScope` is NOT called on success — generating a draft
 * doesn't change any server-side coaching content, only the
 * audit-log run.
 */

export type AIDraftError = {
  /** Structured backend error code, when present. */
  code?: string;
  /** Display message — the backend's `error_message` or a
   * fallback. Never includes raw prompts / provider output. */
  message: string;
  /** Original HTTP status, for callers that need it. */
  status?: number;
};

export type CoachAIDraft = {
  generate: (body: CoachAIDraftRequest) => Promise<CoachAIDraftResponse>;
  isPending: boolean;
  /** The last error in a structured shape, or `null`. */
  error: AIDraftError | null;
  /** Resets the mutation state (clears the last error + text). */
  reset: () => void;
};

export function useCoachAIDraft(): CoachAIDraft {
  const mutation = useMutation({
    mutationFn: (body: CoachAIDraftRequest) => requestAIDraft(body),
  });

  return {
    generate: (body) => mutation.mutateAsync(body),
    isPending: mutation.isPending,
    error: mutation.error ? toAIDraftError(mutation.error) : null,
    reset: () => mutation.reset(),
  };
}

/** Normalize a thrown error into `AIDraftError` — pulls
 * `error_code` + `error_message` out of the unwrapped detail when
 * the response is the FastAPI-shaped failure body. */
function toAIDraftError(err: unknown): AIDraftError {
  if (err instanceof ApiError) {
    const detail = err.detail as
      | CoachAIDraftErrorDetail
      | string
      | null
      | undefined;
    if (detail && typeof detail === "object" && "error_code" in detail) {
      return {
        code: detail.error_code,
        message: detail.error_message || "AI drafting failed.",
        status: err.status,
      };
    }
    return {
      message: typeof detail === "string" ? detail : `HTTP ${err.status}`,
      status: err.status,
    };
  }
  return {
    message: err instanceof Error ? err.message : "AI drafting failed.",
  };
}
